import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Users, Trash2, Shield, Search, Bell, Mail, Copy, Check } from "lucide-react";
import { useTranslation } from "../translations";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUsersPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [sendingNotifs, setSendingNotifs] = useState(false);
  const [notifResult, setNotifResult] = useState(null);
  const [emailDrafts, setEmailDrafts] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const { t } = useTranslation();

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`);
      setUsers(res.data.users || []);
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const deleteUser = async (id) => {
    if (deletingId === id) {
      try {
        await axios.delete(`${API}/admin/users/${id}`);
        setDeletingId(null);
        fetchUsers();
      } catch (err) {
        alert(err.response?.data?.detail || "Erro ao excluir");
        setDeletingId(null);
      }
    } else {
      setDeletingId(id);
      setTimeout(() => setDeletingId(null), 3000);
    }
  };

  const sendWelcomeNotifications = async () => {
    setSendingNotifs(true);
    try {
      const res = await axios.post(`${API}/admin/send-welcome-notifications`);
      setNotifResult(res.data);
    } catch (err) {
      alert("Erro ao enviar notificações");
    }
    setSendingNotifs(false);
  };

  const loadEmailDrafts = async () => {
    try {
      const res = await axios.get(`${API}/admin/email-drafts`);
      setEmailDrafts(res.data.drafts || []);
    } catch (err) {
      alert("Erro ao carregar rascunhos");
    }
  };

  const copyToClipboard = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    }
  };

  const typeLabels = {
    professional: t('professional') || "Profissional",
    employer: t('employer') || "Proprietário",
    admin: "Admin"
  };

  const typeColors = {
    professional: "bg-green-100 text-green-800",
    employer: "bg-blue-100 text-blue-800",
    admin: "bg-purple-100 text-purple-800"
  };

  const statusColors = {
    active: "bg-green-100 text-green-800",
    trial: "bg-yellow-100 text-yellow-800",
    none: "bg-red-100 text-red-800",
    replaced: "bg-gray-100 text-gray-800"
  };

  const filtered = users.filter(u => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (u.name || "").toLowerCase().includes(s) ||
           (u.email || "").toLowerCase().includes(s) ||
           (u.user_type || "").toLowerCase().includes(s);
  });

  if (loading) {
    return (
      <div className="space-y-3" data-testid="users-loading">
        {[...Array(3)].map((_, i) => (
          <Card key={i}><CardContent className="p-4"><div className="h-12 bg-muted rounded animate-pulse" /></CardContent></Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-users-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Users className="w-5 h-5" />
          {t('usersTitle') || 'Gerenciar Usuários'} ({users.length})
        </h2>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('searchUsers') || "Buscar usuário..."}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 text-sm border rounded-lg w-full sm:w-64"
            data-testid="search-users"
          />
        </div>
      </div>

      <div className="space-y-2">
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <Button
            size="sm"
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={sendWelcomeNotifications}
            disabled={sendingNotifs}
            data-testid="send-notifications-btn"
          >
            <Bell className="w-4 h-4 mr-1" />
            {sendingNotifs ? "Enviando..." : "Enviar Notificação Trial"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={loadEmailDrafts}
            data-testid="load-drafts-btn"
          >
            <Mail className="w-4 h-4 mr-1" />
            Gerar Emails para Envio Manual
          </Button>
        </div>

        {/* Notification Result */}
        {notifResult && (
          <Card className="border-green-200 bg-green-50 mb-4">
            <CardContent className="p-3">
              <p className="text-sm font-medium text-green-800">
                {notifResult.notifications_sent > 0
                  ? `${notifResult.notifications_sent} notificações enviadas com sucesso!`
                  : "Todos os usuários já foram notificados anteriormente."}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Email Drafts */}
        {emailDrafts && (
          <Card className="mb-4 border-blue-200">
            <CardHeader className="pb-2 px-4">
              <CardTitle className="text-sm flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Rascunhos de Email ({emailDrafts.length})
              </CardTitle>
              <p className="text-xs text-muted-foreground">Copie e envie cada email pelo Gmail</p>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {emailDrafts.map((draft, i) => (
                  <div key={i} className="p-3 border rounded-lg bg-white">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium">{draft.name}</p>
                        <p className="text-xs text-muted-foreground">{draft.email}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => copyToClipboard(draft.body, i)}
                        >
                          {copiedIdx === i ? <Check className="w-3 h-3 mr-1 text-green-600" /> : <Copy className="w-3 h-3 mr-1" />}
                          {copiedIdx === i ? "Copiado!" : "Copiar"}
                        </Button>
                        <a href={`mailto:${draft.email}?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`}>
                          <Button size="sm" variant="outline" className="h-7 text-xs">
                            <Mail className="w-3 h-3 mr-1" /> Enviar
                          </Button>
                        </a>
                      </div>
                    </div>
                    <details className="text-xs">
                      <summary className="cursor-pointer text-blue-600">Ver texto do email</summary>
                      <div className="mt-2 p-2 bg-gray-50 rounded text-[11px] whitespace-pre-wrap">{draft.body}</div>
                    </details>
                  </div>
                ))}
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="mt-2 text-xs"
                onClick={() => setEmailDrafts(null)}
              >
                Fechar rascunhos
              </Button>
            </CardContent>
          </Card>
        )}

        {filtered.map(user => (
          <Card key={user.id} className="hover:shadow-md transition-shadow" data-testid={`user-${user.id}`}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm sm:text-base">{user.name || "Sem nome"}</p>
                    <Badge className={`text-[10px] ${typeColors[user.user_type] || "bg-gray-100"}`}>
                      {typeLabels[user.user_type] || user.user_type}
                    </Badge>
                    {user.user_type === 'admin' && <Shield className="w-3.5 h-3.5 text-purple-600" />}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {user.subscription ? (
                      <>
                        <Badge variant="outline" className={`text-[10px] ${statusColors[user.subscription.status]}`}>
                          {user.subscription.plan_name || user.subscription.plan_id}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">
                          {user.subscription.status === 'trial' && user.subscription.trial_days
                            ? `Trial ${user.subscription.trial_days}d`
                            : user.subscription.status
                          }
                        </span>
                      </>
                    ) : (
                      <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600">
                        {t('noSubscription') || 'Sem assinatura'}
                      </Badge>
                    )}
                    {user.phone && (
                      <span className="text-[10px] text-muted-foreground">{user.phone}</span>
                    )}
                    <span className="text-[10px] text-muted-foreground">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : '-'}
                    </span>
                  </div>
                </div>
                
                {user.user_type !== 'admin' && (
                  <Button
                    size="sm"
                    variant={deletingId === user.id ? "destructive" : "ghost"}
                    className={`h-9 w-9 p-0 shrink-0 touch-manipulation ${
                      deletingId === user.id ? "animate-pulse" : "text-red-500 hover:text-red-700 hover:bg-red-50"
                    }`}
                    onClick={() => deleteUser(user.id)}
                    title={deletingId === user.id ? "Toque novamente para confirmar" : "Excluir usuário"}
                    data-testid={`delete-user-${user.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        
        {filtered.length === 0 && (
          <p className="text-center text-muted-foreground py-8">
            {search ? (t('noResults') || 'Nenhum resultado encontrado') : (t('statsNoUsers') || 'Nenhum usuário cadastrado')}
          </p>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPanel;
